import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAdminContext } from '../../context/AdminContext.js';
import Swal from 'sweetalert2';
import StatusBadge from '../../components/common/StatusBadge.js';
import { XCircle, ZapOff, History, Briefcase, List, ArrowUpDown, Loader, Search, Zap, MoreVertical } from 'lucide-react';
import { KpiCard } from '../../components/common/KpiCard.js'; 
import AgreementHistoryModal from '../../components/admin/AgreementHistoryModal.js'; 
import Pagination from '../../components/common/Pagination.js';
import AgreementProgressBarVisual from '../../utils/AgreementProgressBar.js';
import '../../styles/InvestmentDetails.css'; 

// --- NUEVO: Componente de Menú Desplegable para Acciones ---
const ActionsDropdown = ({ agreement, onDeactivate, onReactivate, onCancel, onViewHistory, processingId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Cierra el menú si se hace clic fuera de él
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleActionClick = (action) => {
        action(agreement);
        setIsOpen(false); // Cierra el menú después de una acción
    };

    return (
        <div className="actions-dropdown-container" ref={dropdownRef}>
            <button onClick={() => setIsOpen(!isOpen)} className="action-btn-dropdown-toggle" disabled={processingId === agreement.id}>
                <MoreVertical size={18} />
            </button>
            {isOpen && (
                <div className="actions-dropdown-menu">
                    {agreement.acceleratorActive && <button onClick={() => handleActionClick(onDeactivate)} title="Desactiva temporalmente el ROI acelerado para este acuerdo."><ZapOff size={14} /> Desactivar Acelerador</button>}
                    {!agreement.acceleratorActive && <button onClick={() => handleActionClick(onReactivate)} title="Inicia el proceso para volver a activar el acelerador."><Zap size={14} /> Reactivar Acelerador</button>}
                    <button onClick={() => handleActionClick(onViewHistory)} title="Muestra el historial de cambios y eventos de este acuerdo."><History size={14} /> Ver Historial</button>
                    <div className="dropdown-divider"></div>
                    <button onClick={() => handleActionClick(onCancel)} className="danger" title="Inicia el proceso de cancelación y liquidación del acuerdo."><XCircle size={14} /> Cancelar Acuerdo</button>
                </div>
            )}
        </div>
    );
};

const InvestmentDetails = () => {
    const {
        allAgreements,
        loading,
        handleCancelAgreement,
        handleDeactivateAccelerator,
        handleReactivateAccelerator,
        processingId,
        financialLog,
    } = useAdminContext();
    
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [localFilterStatus, setLocalFilterStatus] = useState('todos');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    // --- MEJORA: Estados para el ordenamiento de la tabla ---
    const [sortConfig, setSortConfig] = useState({ key: 'approvedAt', direction: 'desc' });
    // --- MEJORA: Estado para el debouncing de la búsqueda ---
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // --- ESTADOS PARA EL MODAL DE HISTORIAL ---
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [agreementIdForHistory, setAgreementIdForHistory] = useState(null);
    const [userIdForHistory, setUserIdForHistory] = useState(null);

    const roiTransactionsByAgreement = useMemo(() => {
        const map = new Map();
        if (!Array.isArray(financialLog)) {
            return map;
        }

        financialLog.forEach(entry => {
            if (!entry || (entry.type !== 'ganancia_roi' && entry.type !== 'ajuste_manual' && entry.type !== 'reversion_ganancia')) {
                return;
            }

            const agreementId = entry.sourceTransactionId || entry.relatedAgreementId || entry.agreementId;
            if (!agreementId) {
                return;
            }

            const normalizedAmount = Number(entry.amount) || 0;
            const txRecord = {
                type: entry.type,
                amount: normalizedAmount,
                sourceTransactionId: agreementId,
                relatedAgreementId: entry.relatedAgreementId,
                agreementId: entry.agreementId,
                category: entry.category,
                createdAt: entry.createdAt,
            };

            const existing = map.get(agreementId);
            if (existing) {
                existing.push(txRecord);
            } else {
                map.set(agreementId, [txRecord]);
            }
        });

        return map;
    }, [financialLog]);

    // Resetear la página al cambiar filtros
    useEffect(() => {
        setCurrentPage(1);
    }, [localFilterStatus, debouncedSearchTerm]);

    // --- MEJORA: useEffect para implementar debouncing en la búsqueda ---
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(localSearchTerm);
        }, 300); // Espera 300ms después de que el usuario deja de escribir

        return () => clearTimeout(timerId); // Limpia el temporizador si el usuario sigue escribiendo
    }, [localSearchTerm]);

    const confirmAndCancel = (agreement) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a iniciar el proceso para cancelar el acuerdo #${agreement.id.substring(0, 6)}. Se te pedirá un motivo.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Sí, continuar',
            cancelButtonText: 'No, volver'
        }).then((result) => {
            if (result.isConfirmed) {
                handleCancelAgreement(agreement);
            }
        });
    };

    const confirmAndDeactivate = (agreement) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a iniciar el proceso para desactivar el acelerador del acuerdo #${agreement.id.substring(0, 6)}. Se te pedirá un motivo.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ffc107',
            confirmButtonText: 'Sí, continuar',
            cancelButtonText: 'No, volver'
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeactivateAccelerator(agreement);
            }
        });
    };

    const confirmAndReactivate = (agreement) => {
        Swal.fire({
            title: '¿Confirmar Reactivación?',
            text: `Vas a reactivar el acelerador para el acuerdo #${agreement.id.substring(0, 6)}. Esto no tiene costo para el usuario y lo activará inmediatamente.`,
            icon: 'question',
            showCancelButton: true, 
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Sí, reactivar',
            cancelButtonText: 'No, volver'
        }).then((result) => {
            if (result.isConfirmed) {
                handleReactivateAccelerator(agreement);
            }
        });
    };
    // --- FUNCIÓN PARA ABRIR EL MODAL DE HISTORIAL ---
    const handleViewHistory = (agreement) => {
        setAgreementIdForHistory(agreement.id);
        setUserIdForHistory(agreement.userId);
        setShowHistoryModal(true);
    };

    const formatApprovalDate = (agreement) => {
        if (!agreement) {
            return 'Sin registro';
        }

        const { approvedAt, reviewedAt, createdAt } = agreement;
        const statusForMessage = (agreement.displayStatus || agreement.status || '').toLowerCase();

        if (statusForMessage === 'pendiente') {
            return 'Pendiente de aprobación';
        }
        if (statusForMessage === 'rechazado') {
            return 'Rechazado';
        }
        if (statusForMessage === 'cancelado') {
            return 'Cancelado';
        }

        const pickDate = approvedAt || reviewedAt || createdAt;
        if (pickDate?.toDate) {
            return pickDate.toDate().toLocaleDateString('es-ES');
        }
        if (pickDate instanceof Date) {
            return pickDate.toLocaleDateString('es-ES');
        }

        switch (statusForMessage) {
            case 'activo':
            case 'aprobado':
            case 'acelerador_desactivado':
                return 'Aprobado (fecha no disponible)';
            case 'completado':
                return 'Completado (fecha no disponible)';
            default:
                return 'Sin registro';
        }
    };

    // --- MEJORA: Lógica de filtrado y ordenamiento combinada y optimizada ---
    const filteredAgreements = useMemo(() => {
        // 1. Añadir estado de visualización a cada acuerdo
        const agreementsWithDisplayStatus = (allAgreements || []).map(a => {
            const isCompleted = (a.currentValue || 0) >= (a.netInvestmentAmount || a.amount) * 2;
            let displayStatus = a.status;
            if (displayStatus === 'aprobado') {
                displayStatus = isCompleted ? 'completado' : 'activo';
            }
            return { ...a, displayStatus };
        });

        // 2. Filtrar
        let filtered = agreementsWithDisplayStatus.filter(a => {
            // --- LÓGICA DE FILTRADO CORREGIDA ---
            const searchMatch = !debouncedSearchTerm || (a.userEmail && a.userEmail.toLowerCase().startsWith(debouncedSearchTerm.toLowerCase()));
            if (!searchMatch) {
                return false; // Si no coincide con la búsqueda, lo descartamos inmediatamente.
            }

            // Ahora aplicamos el filtro de estado
            if (localFilterStatus === 'todos') return true; // Si es 'todos' y ya pasó el search, lo incluimos.
            if (localFilterStatus === 'acelerador_desactivado') return a.acceleratorActive === false && a.deactivationReason;
            
            return a.displayStatus === localFilterStatus;
        });

        // 3. Ordenar
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];

                // Manejar fechas (objetos Timestamp de Firebase)
                if (valA && typeof valA.toDate === 'function') valA = valA.toDate();
                if (valB && typeof valB.toDate === 'function') valB = valB.toDate();

                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [allAgreements, localFilterStatus, debouncedSearchTerm, sortConfig]);
    
    // --- NUEVO: Cálculo de estadísticas para los KPIs ---
    const filteredStats = useMemo(() => {
        const totalCapital = filteredAgreements.reduce((sum, a) => sum + (a.netInvestmentAmount || a.amount || 0), 0);
        const totalCount = filteredAgreements.length;
        return { totalCapital, totalCount };
    }, [filteredAgreements]);

    // --- MEJORA: Función para manejar el clic en las cabeceras de la tabla para ordenar ---
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // --- MEJORA: Componente para las cabeceras de la tabla que permite ordenar ---
    function SortableHeader({ children, sortKey }) {
        return (
            <th onClick={() => requestSort(sortKey)} className="sortable-header">
                {children}
                {sortConfig.key === sortKey ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : <ArrowUpDown size={14} style={{ marginLeft: '4px', opacity: 0.5 }} />}
            </th>
        );
    }

    if (loading) {
        return <p>Cargando detalles de participaciones...</p>;
    }

    // --- PAGINACIÓN LOCAL SOBRE LOS DATOS FILTRADOS ---
    const totalPages = Math.ceil(filteredAgreements.length / itemsPerPage);
    const paginatedAgreements = filteredAgreements.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    return (
        <div className="main-panel">
            <h2 className="section-title">Detalles de Todas las Participaciones</h2>
            <p>Aquí puedes ver, filtrar y buscar todos los acuerdos comerciales de la plataforma.</p>

            {/* --- NUEVO: Resumen con KPI Cards --- */}
            <div className="kpi-grid" style={{ marginTop: '1.5rem' }}>
                <KpiCard 
                    title="Nº de Acuerdos (Filtrados)" 
                    value={filteredStats.totalCount} 
                    icon={<List size={24} />} 
                    color="#9333ea" 
                />
                <KpiCard 
                    title="Capital Total (Filtrado)" 
                    value={`$${filteredStats.totalCapital.toFixed(2)}`} 
                    icon={<Briefcase size={24} />} 
                    color="#2563eb" 
                />
            </div>

            {/* --- CONTROLES DE FILTRO SIN BOTÓN --- */}
            <div className="report-filters" style={{ marginTop: '1.5rem' }}>
                <div className="filter-item">
                    <Search size={16} />
                    <input 
                        type="text" 
                        placeholder="Buscar por inicio de email..." 
                        value={localSearchTerm} 
                        onChange={(e) => setLocalSearchTerm(e.target.value)} 
                    />
                </div>
                <div className="filter-item">
                    <select value={localFilterStatus} onChange={(e) => setLocalFilterStatus(e.target.value)} disabled={loading} style={{ border: 'none', background: 'transparent', outline: 'none' }}>
                        <option value="todos">Todos los Estados</option>
                        <option value="activo">Activos</option>
                        <option value="completado">Completados</option> 
                        <option value="pendiente">Pendientes</option>
                        <option value="rechazado">Rechazados</option>
                        <option value="cancelado">Cancelados</option> {/* <-- AÑADIDO */}
                        <option value="acelerador_desactivado">Acelerador Desactivado</option> {/* <-- AÑADIDO */}
                    </select>
                </div>
            </div>

            <div className="report-table-container">
                <table className="report-table">
                    <thead> 
                        <tr>
                            <SortableHeader sortKey="userEmail">Usuario</SortableHeader>
                            <SortableHeader sortKey="amount">Monto</SortableHeader>
                            <SortableHeader sortKey="netInvestmentAmount">Monto neto</SortableHeader>
                            <SortableHeader sortKey="approvedAt">Fecha Aprobación</SortableHeader>
                            <th>Acelerador</th><th>Progreso</th><SortableHeader sortKey="displayStatus">Estado</SortableHeader>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="10" className="empty-state-text">Cargando...</td></tr>
                        ) : paginatedAgreements.length === 0 ? (
                            <tr><td colSpan="10" className="empty-state-text">No se encontraron acuerdos con los filtros aplicados.</td></tr>
                        ) : (
                            paginatedAgreements.map(agreement => (
                                <tr key={agreement.id}>
                                    <td>{agreement.userEmail}</td><td>${(agreement.amount || 0).toFixed(2)}</td><td>${(agreement.netInvestmentAmount || agreement.amount || 0).toFixed(2)}</td>
                                    <td>{formatApprovalDate(agreement)}</td><td>{agreement.acceleratorActive ? 'Sí' : 'No'}</td>
                                    <td>
                                        <AgreementProgressBarVisual
                                            transactions={roiTransactionsByAgreement.get(agreement.id) || []}
                                            agreement={agreement} 
                                            styleType="table" // Usa el estilo de tabla
                                        />
                                    </td>
                                    <td><StatusBadge status={agreement.displayStatus} variant="table" /></td>
                                    <td>
                                        {processingId === agreement.id ? (
                                            <div className="action-buttons-wrapper">
                                                <Loader size={16} className="spinner" />
                                            </div>
                                        ) : (
                                            agreement.status === 'aprobado' && (
                                                <ActionsDropdown
                                                    agreement={agreement}
                                                    onDeactivate={confirmAndDeactivate}
                                                    onReactivate={confirmAndReactivate}
                                                    onCancel={confirmAndCancel}
                                                    onViewHistory={handleViewHistory}
                                                    processingId={processingId}
                                                />
                                            )
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* La paginación ahora se muestra siempre que haya acuerdos */}
            {filteredAgreements.length > 0 && totalPages > 1 && (
                <Pagination
                    totalItems={filteredAgreements.length}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={setItemsPerPage}
                    currentPage={currentPage}
                    onNext={handleNextPage}
                    onPrevious={handlePreviousPage}
                    canNext={currentPage < totalPages}
                    canPrevious={currentPage > 1}
                />
            )}

            {/* --- RENDERIZAR EL MODAL DE HISTORIAL --- */}
            {showHistoryModal && (
                <AgreementHistoryModal
                    agreementId={agreementIdForHistory}
                    userId={userIdForHistory}
                    onClose={() => setShowHistoryModal(false)}
                />
            )}

        </div>
    );
};

export default InvestmentDetails;
